import React, { useState, useEffect } from "react";
import { t as T } from "i18next";
import Layout from "../components/Layout";
import { useAuth } from "../contexts/AuthContext";
import { Navigate } from "react-router-dom";
import { Menu } from "lucide-react";
import CategoryBar from "../components/beta/CategoryBar";
import HeroStripe from "../components/beta/HeroStripe";
import FeedList from "../components/beta/FeedList";
import CreatorRail from "../components/beta/CreatorRail";
import BetaSidebar from "../components/beta/BetaSidebar";
import BetaTour from "../components/beta/BetaTour";
import PostComposer from "../components/beta/PostComposer";

/**
 * BetaForside — FYP-style forside bak /beta.
 * Desktop (≥lg): 18%-50%-32% grid med venstre sidebar + feed + høyre paid-rail.
 * Mobil: enkel feed-kolonne. Hamburger-knapp eller swipe-fra-venstre-kant åpner drawer.
 */
export default function BetaForside() {
  const { user, loading } = useAuth();
  const [selected, setSelected] = useState([]);
  const [followMode, setFollowMode] = useState("all");
  const [reloadKey, setReloadKey] = useState(0);
  const [search, setSearch] = useState("");
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Swipe-fra-venstre-kant for å åpne drawer (kun mobil)
  useEffect(() => {
    let startX = 0, startY = 0, tracking = false;
    const onStart = (e) => {
      if (window.innerWidth >= 1024) return;
      if (drawerOpen) return;
      const t = e.touches && e.touches[0];
      if (!t) return;
      if (t.clientX > 25) return; // må starte helt venstre
      startX = t.clientX;
      startY = t.clientY;
      tracking = true;
    };
    const onMove = (e) => {
      if (!tracking) return;
      const t = e.touches && e.touches[0];
      if (!t) return;
      const dx = t.clientX - startX;
      const dy = Math.abs(t.clientY - startY);
      if (dx > 55 && dy < 50) {
        setDrawerOpen(true);
        tracking = false;
      }
    };
    const onEnd = () => { tracking = false; };
    window.addEventListener("touchstart", onStart, { passive: true });
    window.addEventListener("touchmove", onMove, { passive: true });
    window.addEventListener("touchend", onEnd, { passive: true });
    return () => {
      window.removeEventListener("touchstart", onStart);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onEnd);
    };
  }, [drawerOpen]);

  if (loading) return null;
  if (!user) return <Navigate to="/" replace />;

  const toggleCategory = (key) => {
    setSelected((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  };

    return (
      <Layout withSidebar={false} wide noNav>
        {/* Mobil: flytende hamburger-knapp (kun mobil, under drawer-z) */}
        <button
          onClick={() => setDrawerOpen(true)}
          className="lg:hidden fixed top-16 left-3 z-40 p-2 rounded-md bg-[#141116] border-2 border-[var(--magenta)] text-[var(--cream)] shadow-lg"
          aria-label={T("ui.open_menu")}
          data-testid="beta-mobile-menu"
          style={{ boxShadow: "0 0 14px rgba(255, 95, 168, 0.4)" }}
        >
          <Menu className="h-5 w-5" />
        </button>

        <div className="w-full px-4 lg:px-8" data-testid="beta-forside">
          <div className="lg:grid lg:grid-cols-[18%_50%_32%] lg:gap-6">
            {/* Venstre: fast sidebar på desktop, drawer på mobil */}
            <BetaSidebar drawerOpen={drawerOpen} onCloseDrawer={() => setDrawerOpen(false)} />

            {/* Hovedfeed-kolonne */}
            <main className="min-w-0">
              <CategoryBar
                selected={selected}
                onToggle={toggleCategory}
                followMode={followMode}
                onFollowMode={setFollowMode}
                searchValue={search}
                onSearch={setSearch}
              />
              <HeroStripe />
              <div data-testid="beta-home-composer">
                <PostComposer onPosted={() => setReloadKey((k) => k + 1)} />
              </div>
              <FeedList selected={selected} followMode={followMode} search={search} reloadKey={reloadKey} />
              <BetaTour />
            </main>

            {/* Høyre rail: 4 betalte creator-slots (kun desktop) */}
            <div className="hidden lg:block">
              <CreatorRail />
            </div>
          </div>
        </div>
      </Layout>
    );
  }
